import React, { useEffect, useRef, useState } from 'react';
import L from "leaflet";
import markerIconPng from "leaflet/dist/images/marker-icon.png";
import { Icon } from 'leaflet';
import ReactDOM from 'react-dom/client';

// Roughly the centre of Sweden, which is forest outside Östersund. Only ever used to
// frame an empty map, never as a position: a map has to open somewhere, but a form must
// not submit a coordinate nobody chose.
//
// It used to be both, and at street zoom. A form that was saved untouched stored it
// outright, and anyone placing a pin was shown this forest close up and clicked in it,
// so practices and events alike ended up in Jämtland. Opening on the whole country
// instead means a click is always a choice about where in Sweden the place is.
const SWEDEN_CENTRE = { latitude: 63.33, longitude: 14.06 };
const COUNTRY_ZOOM = 4;
const PLACE_ZOOM = 12;

const hasCoordinate = (value) => value !== null && value !== undefined && value !== '';

const LocationPicker = ({ locationId, onLocationChange, initialLatitude, initialLongitude, fieldNamePrefix, clearable, fallbackCentre }) => {
    // Refs, not state: the Leaflet click and drag handlers are bound once and keep the
    // values they closed over, so the marker they see has to be the one the clear button
    // removes. The cleanup below needs the same for the map.
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const hasInitialPosition = hasCoordinate(initialLatitude) && hasCoordinate(initialLongitude);
    const [position, setPosition] = useState(
        hasInitialPosition ? { latitude: initialLatitude, longitude: initialLongitude } : null
    );
    const mapId = `location-picker-map-${locationId}`;

    const moveTo = (lat, lng) => {
        setPosition({ latitude: lat, longitude: lng });
        if (onLocationChange) {
            onLocationChange({ latitude: lat, longitude: lng });
        }
    };

    const clearPosition = () => {
        if (markerRef.current) {
            markerRef.current.remove();
            markerRef.current = null;
        }
        // Nothing to report to a parent: the only picker with this button is the
        // standalone one, which speaks through the hidden inputs that dropping the
        // position removes. LocationManager has its own clear control beside each place,
        // and its "no exact position" is 0,0 rather than the event form's absent field —
        // so anything wiring clearable through to it has to say which of the two it means.
        setPosition(null);
    };

    useEffect(() => {
        if (!mapRef.current) {
            // Initialize map. A place with no pin of its own still opens on the locality
            // it belongs to, so the click that sets the pin lands near the right town
            // rather than wherever the map happened to start.
            const view = position ?? fallbackCentre ?? SWEDEN_CENTRE;
            const knownPlace = position ?? fallbackCentre;
            const mapInstance = L.map(mapId)
                .setView([view.latitude, view.longitude], knownPlace ? PLACE_ZOOM : COUNTRY_ZOOM);
            L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(mapInstance);
            mapRef.current = mapInstance;

            // Create marker icon
            const markerIcon = new Icon({
                iconUrl: markerIconPng,
                iconSize: [25, 41],
                iconAnchor: [12, 41]
            });

            // A marker is draggable wherever it came from, so it needs the handler
            // wherever it is created — not only when the form opened with a position.
            const placeMarker = (lat, lng) => {
                const instance = L.marker([lat, lng], {
                    icon: markerIcon,
                    draggable: true
                }).addTo(mapInstance);
                instance.on('dragend', (e) => {
                    const { lat: dragLat, lng: dragLng } = e.target.getLatLng();
                    moveTo(dragLat, dragLng);
                });
                markerRef.current = instance;
                return instance;
            };

            // Add initial marker if coordinates provided
            if (position) {
                placeMarker(position.latitude, position.longitude);
            }

            // Handle map clicks
            mapInstance.on('click', (e) => {
                const { lat, lng } = e.latlng;
                if (markerRef.current) {
                    markerRef.current.setLatLng([lat, lng]);
                } else {
                    placeMarker(lat, lng);
                }
                moveTo(lat, lng);
            });
        }

        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
                markerRef.current = null;
            }
        };
    }, []);

    return (
        <div className="location-picker-container">
            <div id={mapId} style={{ height: '400px', marginBottom: '10px' }}></div>
            {/* No marker means no position was chosen, so the form submits nothing and the
                event keeps no coordinates, rather than inheriting the map's opening view. */}
            {position && (
                <>
                    <input
                        type="hidden"
                        name={fieldNamePrefix != null ? `${fieldNamePrefix}latitude` : `locations[${locationId}].latitude`}
                        value={position.latitude}
                    />
                    <input
                        type="hidden"
                        name={fieldNamePrefix != null ? `${fieldNamePrefix}longitude` : `locations[${locationId}].longitude`}
                        value={position.longitude}
                    />
                </>
            )}
            {/* What happens without a pin differs by caller, and saying the wrong one
                pushes an admin into dropping an approximate pin — the very thing this
                picker was changed to stop. An event with no pin shows no map at all; a
                practice falls back to its locality and still appears. */}
            <div className="form-text">
                {position
                    ? 'Klicka på kartan eller dra nålen för att flytta platsen.'
                    : clearable
                        ? 'Klicka på kartan för att sätta ut platsen. Utan nål visas ingen karta på evenemanget.'
                        : 'Klicka på kartan bara om du vill ange en exakt adress. Utan nål visas platsen ändå, på orten du valt.'}
            </div>
            {clearable && position && (
                <button
                    type="button"
                    className="btn btn-sm btn-outline-danger mt-2"
                    onClick={clearPosition}
                >
                    <i className="fa-solid fa-xmark"></i> Ta bort kartposition
                </button>
            )}
        </div>
    );
};

// Function to create a LocationPicker instance
// Standalone use (the event form) owns its own position, so it carries the clear button.
// LocationManager has one of its own next to each place and passes nothing.
window.createLocationPicker = (containerId, locationId, initialLat, initialLng, fieldNamePrefix) => {
    const container = document.getElementById(containerId);
    if (container) {
        const root = ReactDOM.createRoot(container);
        root.render(
            <LocationPicker
                locationId={locationId}
                initialLatitude={initialLat}
                initialLongitude={initialLng}
                fieldNamePrefix={fieldNamePrefix}
                clearable
            />
        );
    }
};

export default LocationPicker; 